import React, { useEffect, useMemo, useState, useRef } from "react";
import { Button, Chip, TextField, CircularProgress, Tooltip, IconButton } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import "jspdf-autotable";
import "./review.css";

/**
 * Review Table Component (Costing Sheet style)
 * Features:
 * - Custom HTML table matching Costing Sheet design
 * - Draggable column resize
 * - Draggable row height resize
 * - Bulk upload for Customer Response and Comment columns
 */

// Constants for tooltip display
const TOOLTIP_CLAUSE_MAX_LENGTH = 100;
const TOOLTIP_TEXT_MAX_LENGTH = 80;

function CollapsibleText({ text = "", lines = 3 }) {
  const [open, setOpen] = useState(false);
  if (!text) return <span style={{ color: "#94a3b8" }}>—</span>;
  const plain = String(text).trim();
  const snippet = plain.length > 360 ? plain.slice(0, 360) : plain;
  const isTrunc = plain.length > snippet.length || plain.split("\n").length > lines;
  return (
    <div className="collapsible">
      <div className={`collapsible-content ${open ? "open" : ""}`} style={{ whiteSpace: "pre-wrap", textAlign: "left" }}>
        {open ? plain : snippet + (isTrunc ? "…" : "")}
      </div>
      {isTrunc && (
        <button className="collapsible-toggle" onClick={() => setOpen((s) => !s)}>
          {open ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// Suggestion Tooltip Component - shows previous comments/responses for similar clauses
// ----------------------------------------------------------------------
function SuggestionTooltip({ suggestions, onApply }) {
  if (!suggestions || suggestions.length === 0) return null;
  
  return (
    <Tooltip
      arrow
      placement="right"
      title={
        <div className="suggestion-tooltip-content">
          <div className="suggestion-header">
            <LightbulbIcon style={{ fontSize: 16, marginRight: 4 }} />
            <strong>Similar clauses found:</strong>
          </div>
          {suggestions.map((s, idx) => (
            <div key={idx} className="suggestion-item">
              <div className="suggestion-clause" title={s.clauseText}>
                {s.clauseText.substring(0, TOOLTIP_CLAUSE_MAX_LENGTH)}...
              </div>
              {s.comment && (
                <div className="suggestion-field">
                  <span className="suggestion-label">Comment:</span>
                  <span className="suggestion-value">{s.comment.substring(0, TOOLTIP_TEXT_MAX_LENGTH)}...</span>
                  {onApply && (
                    <button 
                      className="suggestion-apply-btn"
                      onClick={(e) => { e.stopPropagation(); onApply(s.comment, 'comment'); }}
                    >
                      Use
                    </button>
                  )}
                </div>
              )}
              {s.customerResponse && (
                <div className="suggestion-field">
                  <span className="suggestion-label">Response:</span>
                  <span className="suggestion-value">{s.customerResponse.substring(0, TOOLTIP_TEXT_MAX_LENGTH)}...</span>
                </div>
              )}
            </div>
          ))}
        </div>
      }
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: '#1e293b',
            maxWidth: 400,
            p: 1.5,
            '& .MuiTooltip-arrow': {
              color: '#1e293b',
            },
          },
        },
      }}
    >
      <span className="suggestion-indicator">
        <LightbulbIcon style={{ fontSize: 16, color: '#f59e0b' }} />
      </span>
    </Tooltip>
  );
}

// ----------------------------------------------------------------------
// Upload Button Component with highly robust parsing logic
// ----------------------------------------------------------------------
function UploadButton({ onUpload, revIndex, isUploading, targetType = "response" }) {
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const fileContent = e.target.result.trim();
      const lines = fileContent.split('\n');
      if (lines.length < 2) {
        alert("The uploaded file is empty or contains no data rows.");
        return;
      }
      
      // Helper function for robust CSV splitting (handles commas inside quotes)
      const split = (line) => line.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/g).map(h => h.trim().replace(/^"|"$/g, ''));
      
      const headers = split(lines[0]);
      
      // Normalize header names for matching (remove spaces, lowercase)
      const normalize = (s) => s.toLowerCase().replace(/\s+/g, '');
      
      // Expected Headers (Normalized)
      const targetHeaderNormalized = normalize(targetType === "response"
        ? `Customer Response Rev ${revIndex}`
        : `Comment Rev ${revIndex}`);
      const srHeaderNormalized = 'srno'; // The fixed Sr No header name
      
      // Find indexes using normalized headers
      const srIndex = headers.findIndex(h => normalize(h) === srHeaderNormalized);
      const targetIndex = headers.findIndex(h => normalize(h) === targetHeaderNormalized);
      
      if (srIndex === -1 || targetIndex === -1) {
        alert(`Could not find 'Sr No' (index ${srIndex}) or '${targetHeaderNormalized}' (index ${targetIndex}) in CSV headers. Check headers in your file: ${headers.map(normalize).join(", ")}`);
        return;
      }
      
      // Map: [{sr, value}] - only include rows with non-blank values
      const bulkData = lines.slice(1).map(line => {
        const parts = split(line);
        // Ensure both fields exist before trying to read
        if (parts.length > srIndex && parts.length > targetIndex) {
            const value = parts[targetIndex];
            // Skip rows with blank/empty values in the target column
            if (!value || value.trim() === '') {
              return null;
            }
            return {
              sr: parts[srIndex],
              value: value.trim(),
            };
        }
        return null;
      }).filter(row => row && row.sr); // Must have an Sr No to be a valid row
      
      // Pass the response array and the target revision index
      onUpload(bulkData, revIndex, targetType); 
    };
    
    reader.onerror = () => alert("Error reading file.");
    reader.readAsText(file);
  };
  
  return (
    <Button
      variant="contained"
      size="small"
      component="label" 
      startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
      disabled={isUploading}
      sx={{
        backgroundColor: '#075985',
        '&:hover': { backgroundColor: '#054a6b' },
        fontSize: '0.7rem',
        padding: '2px 8px',
        minWidth: 'auto',
        ml: 1,
      }}
    >
      {isUploading ? "Processing..." : `Bulk Upload`}
      <input
        type="file"
        hidden
        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        onChange={handleFileChange}
        onClick={e => { e.target.value = null; }}
      />
    </Button>
  );
}

// ----------------------------------------------------------------------
// Main Review component with Costing Sheet style table
// ----------------------------------------------------------------------
export default function Review({
  tender,
  clauses = [],
  onUpdateStatus = () => {},
  onSaveComment = async () => {},
  onSaveCustomerResponse = null,
  onAddRevision = null,
  onBulkUploadResponse = async () => {},
  findSimilarClauses = () => [],
}) {
  const tableRef = useRef(null);
  
  // Revision management
  const tenderRevisions = useMemo(() => {
    if (Array.isArray(tender?.revisions) && tender.revisions.length > 0) {
      return tender.revisions.map((r) => (typeof r === "string" ? r : r.name || r));
    }
    return ["Rev 0"];
  }, [tender]);
  
  const parseRevIndex = (name) => {
    const m = (name || "").match(/Rev\s*(\d+)/i);
    return m ? Number(m[1]) : null;
  };
  
  const revIndices = tenderRevisions.map(parseRevIndex).filter((v) => v !== null);
  const maxRevIndex = revIndices.length ? Math.max(...revIndices) : 0;
  
  const buildColumnsMeta = (maxIndex) => {
    const cols = [];
    for (let i = 0; i <= Math.max(0, maxIndex); i++) {
      cols.push({ id: `comment-${i}`, type: "comment", revIndex: i, label: `Comment Rev ${i}`, visible: i === maxRevIndex });
      cols.push({ id: `response-${i}`, type: "response", revIndex: i, label: `Customer Response Rev ${i}`, visible: i === maxRevIndex });
    }
    return cols;
  };
  
  const [columnsMeta, setColumnsMeta] = useState(() => buildColumnsMeta(maxRevIndex));
  const [colVisibility, setColVisibility] = useState(() =>
    columnsMeta.reduce((acc, c) => ({ ...acc, [c.id]: c.visible }), {})
  );
  const [selectedColumnId, setSelectedColumnId] = useState(columnsMeta[0]?.id || null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [selectedRevision, setSelectedRevision] = useState(maxRevIndex);

  // Comment and response state
  const [commentDrafts, setCommentDrafts] = useState({});
  const [responseDrafts, setResponseDrafts] = useState({});
  const [savingState, setSavingState] = useState({});
  const [responseSavingState, setResponseSavingState] = useState({});
  const [isUploading, setIsUploading] = useState(false);

  // AI generation loading state - tracks which cells are generating
  const [aiGeneratingState, setAiGeneratingState] = useState({});

  // Column widths for resizing
  const [columnWidths, setColumnWidths] = useState({
    checkbox: 40,
    sr: 50,
    clause: 350,
    status: 100,
  });
  const [resizingCol, setResizingCol] = useState(null); // { column, startX, startWidth }

  // Row heights for resizing - individual row heights
  const [rowHeights, setRowHeights] = useState({}); // { rowId: height }
  const [defaultRowHeight, setDefaultRowHeight] = useState(180);
  const [resizingRow, setResizingRow] = useState(null); // { rowId, startY, startHeight }

  // Selected rows for checkbox
  const [selectedRows, setSelectedRows] = useState({});

  // Suggestions state - computed for each clause
  const clauseSuggestions = useMemo(() => {
    const suggestions = {};
    clauses.forEach((clause) => {
      if (clause.text) {
        const matches = findSimilarClauses(clause.text);
        if (matches && matches.length > 0) {
          suggestions[clause.id] = matches;
        }
      }
    });
    return suggestions;
  }, [clauses, findSimilarClauses]);

  useEffect(() => {
    setColumnsMeta((prev) => {
      const prevMax = prev.reduce((m, c) => Math.max(m, c.revIndex), 0);
      const targetMax = Math.max(prevMax, maxRevIndex);
      if (targetMax === prevMax) return prev;
      const next = [...prev];
      for (let i = prevMax + 1; i <= targetMax; i++) {
        next.push({ id: `comment-${i}`, type: "comment", revIndex: i, label: `Comment Rev ${i}`, visible: i === targetMax });
        next.push({ id: `response-${i}`, type: "response", revIndex: i, label: `Customer Response Rev ${i}`, visible: i === targetMax });
      }
      return next;
    });
  }, [maxRevIndex]);

  useEffect(() => {
    setColVisibility((prev) => {
      const next = { ...prev };
      columnsMeta.forEach((c) => {
        if (!(c.id in next)) next[c.id] = c.visible ?? true;
      });
      return next;
    });
  }, [columnsMeta]);

  useEffect(() => {
    setSelectedRevision((prev) => {
      if (prev > maxRevIndex || prev === undefined) {
        return maxRevIndex;
      }
      return prev;
    });
  }, [maxRevIndex]);

  // Initialize column widths for dynamic columns
  useEffect(() => {
    setColumnWidths(prev => {
      const next = { ...prev };
      columnsMeta.forEach(col => {
        if (!(col.id in next)) {
          next[col.id] = 280;
        }
      });
      return next;
    });
  }, [columnsMeta]);

  // Column resize handlers
  const handleColResizeStart = (e, column) => {
    e.preventDefault();
    setResizingCol({
      column,
      startX: e.clientX,
      startWidth: columnWidths[column] || 200
    });
  };

  // Row resize handlers
  const handleRowResizeStart = (e, rowId) => {
    e.preventDefault();
    e.stopPropagation();
    const currentHeight = rowHeights[rowId] || defaultRowHeight;
    setResizingRow({
      rowId,
      startY: e.clientY,
      startHeight: currentHeight
    });
  };

  // Handle mouse move for both column and row resize
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (resizingCol) {
        const diff = e.clientX - resizingCol.startX;
        const newWidth = Math.max(60, resizingCol.startWidth + diff);
        setColumnWidths(prev => ({
          ...prev,
          [resizingCol.column]: newWidth
        }));
      }
      if (resizingRow) {
        const diff = e.clientY - resizingRow.startY;
        const newHeight = Math.max(80, Math.min(600, resizingRow.startHeight + diff));
        setRowHeights(prev => ({
          ...prev,
          [resizingRow.rowId]: newHeight
        }));
      }
    };

    const handleMouseUp = () => {
      setResizingCol(null);
      setResizingRow(null);
    };

    if (resizingCol || resizingRow) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = resizingCol ? 'col-resize' : 'row-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [resizingCol, resizingRow]);

  // Data getters and handlers
  const getCommentValue = (clause, revIndex) => {
    const revName = `Rev ${revIndex}`;
    if (!clause) return "";
    if (clause.comments && clause.comments[revName]) return clause.comments[revName];
    if (clause.commentsByRevision && clause.commentsByRevision[revName]) return clause.commentsByRevision[revName];
    if (revIndex === 0) return clause.comment || "";
    return "";
  };

  const getResponseValue = (clause, revIndex) => {
    const revName = `Rev ${revIndex}`;
    if (!clause) return "";
    return (clause.customerResponses && clause.customerResponses[revName]) || "";
  };

  const handleCommentChange = (clauseId, revIndex, val) => {
    setCommentDrafts((p) => {
      const next = { ...p };
      if (!next[clauseId]) next[clauseId] = {};
      next[clauseId][`Rev ${revIndex}`] = val;
      return next;
    });
    setSavingState((s) => ({ ...s, [clauseId]: { ...(s[clauseId] || {}), [`comment-${revIndex}`]: "idle" } }));
  };

  const saveComment = async (clause, revIndex) => {
    if (!clause) return;
    const clauseId = clause.id ?? clause._id ?? clause.tempId;
    const revName = `Rev ${revIndex}`;
    const draft = (commentDrafts[clauseId] && commentDrafts[clauseId][revName]) ?? getCommentValue(clause, revIndex);
    const colId = `comment-${revIndex}`;
    setSavingState((s) => ({ ...s, [clauseId]: { ...(s[clauseId] || {}), [colId]: "saving" } }));
    try {
      const updatedClause = { ...clause, comments: { ...(clause.comments || {}), [revName]: draft } };
      await onSaveComment(updatedClause);
      setSavingState((s) => ({ ...s, [clauseId]: { ...(s[clauseId] || {}), [colId]: "saved" } }));
      setCommentDrafts((p) => {
        const next = { ...p };
        if (!next[clauseId]) next[clauseId] = {};
        next[clauseId][revName] = draft;
        return next;
      });
      setTimeout(() => {
        setSavingState((s) => ({ ...s, [clauseId]: { ...(s[clauseId] || {}), [colId]: "idle" } }));
      }, 1200);
    } catch (err) {
      setSavingState((s) => ({ ...s, [clauseId]: { ...(s[clauseId] || {}), [colId]: "error" } }));
      alert("Failed to save comment.");
    }
  };

  const handleResponseChange = (clauseId, revIndex, val) => {
    setResponseDrafts((p) => {
      const next = { ...p };
      if (!next[clauseId]) next[clauseId] = {};
      next[clauseId][`Rev ${revIndex}`] = val;
      return next;
    });
    setResponseSavingState((s) => ({ ...s, [clauseId]: { ...(s[clauseId] || {}), [`response-${revIndex}`]: "idle" } }));
  };

  const saveResponse = async (clause, revIndex) => {
    if (!clause) return;
    const clauseId = clause.id ?? clause._id ?? clause.tempId;
    const revName = `Rev ${revIndex}`;
    const draft = (responseDrafts[clauseId] && responseDrafts[clauseId][revName]) ?? getResponseValue(clause, revIndex);
    const colId = `response-${revIndex}`;
    setResponseSavingState((s) => ({ ...s, [clauseId]: { ...(s[clauseId] || {}), [colId]: "saving" } }));
    try {
      if (typeof onSaveCustomerResponse === "function") {
        await onSaveCustomerResponse(clauseId, revName, draft);
      } else {
        const updatedClause = { ...clause, customerResponses: { ...(clause.customerResponses || {}), [revName]: draft } };
        await onSaveComment(updatedClause);
      }
      setResponseSavingState((s) => ({ ...s, [clauseId]: { ...(s[clauseId] || {}), [colId]: "saved" } }));
      setResponseDrafts((p) => {
        const next = { ...p };
        if (!next[clauseId]) next[clauseId] = {};
        next[clauseId][revName] = draft;
        return next;
      });
      setTimeout(() => {
        setResponseSavingState((s) => ({ ...s, [clauseId]: { ...(s[clauseId] || {}), [colId]: "idle" } }));
      }, 1200);
    } catch (err) {
      setResponseSavingState((s) => ({ ...s, [clauseId]: { ...(s[clauseId] || {}), [colId]: "error" } }));
      alert("Failed to save response.");
    }
  };

  // AI Comment Generation for revision comments
  const handleAIGenerateRevisionComment = async (clause, revIndex) => {
    if (!clause) return;
    const clauseId = clause.id ?? clause._id ?? clause.tempId;
    const revName = `Rev ${revIndex}`;
    const stateKey = `${clauseId}-comment-${revIndex}`;
    
    // Get the current user input (draft or saved)
    const userInput = (commentDrafts[clauseId] && commentDrafts[clauseId][revName]) ?? getCommentValue(clause, revIndex) ?? "";
    
    // Gather previous comments (from all previous revisions)
    const previousComments = {};
    for (let i = 0; i < revIndex; i++) {
      const prevRevName = `Rev ${i}`;
      const prevComment = getCommentValue(clause, i);
      if (prevComment && prevComment.trim()) {
        previousComments[prevRevName] = prevComment;
      }
    }
    
    // Gather previous customer responses (from all previous revisions)
    const previousResponses = {};
    for (let i = 0; i < revIndex; i++) {
      const prevRevName = `Rev ${i}`;
      const prevResponse = getResponseValue(clause, i);
      if (prevResponse && prevResponse.trim()) {
        previousResponses[prevRevName] = prevResponse;
      }
    }
    
    // Set loading state
    setAiGeneratingState((prev) => ({ ...prev, [stateKey]: true }));
    
    try {
      const response = await fetch("/api/generate-comments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "revision",
          clauseText: clause.text || "",
          previousComments,
          previousResponses,
          userInput,
          revisionNumber: revIndex,
          tenderTitle: tender?.title || tender?.clientName || "",
          tenderDescription: tender?.description || "",
        }),
      });

      if (!response.ok) throw new Error("AI Comment Generation Failed.");

      const data = await response.json();
      const aiComment = data.comment || "AI failed to generate a useful comment.";

      // Update the draft with AI-generated comment
      setCommentDrafts((prev) => {
        const next = { ...prev };
        if (!next[clauseId]) next[clauseId] = {};
        next[clauseId][revName] = aiComment;
        return next;
      });
      
      // Auto-save the generated comment
      setSavingState((s) => ({ ...s, [clauseId]: { ...(s[clauseId] || {}), [`comment-${revIndex}`]: "saving" } }));
      try {
        const updatedClause = { ...clause, comments: { ...(clause.comments || {}), [revName]: aiComment } };
        await onSaveComment(updatedClause);
        setSavingState((s) => ({ ...s, [clauseId]: { ...(s[clauseId] || {}), [`comment-${revIndex}`]: "saved" } }));
        setTimeout(() => {
          setSavingState((s) => ({ ...s, [clauseId]: { ...(s[clauseId] || {}), [`comment-${revIndex}`]: "idle" } }));
        }, 1200);
      } catch (saveErr) {
        setSavingState((s) => ({ ...s, [clauseId]: { ...(s[clauseId] || {}), [`comment-${revIndex}`]: "error" } }));
      }
    } catch (error) {
      console.error("handleAIGenerateRevisionComment error:", error);
      alert("Failed to generate AI comment. Please try again.");
    } finally {
      setAiGeneratingState((prev) => ({ ...prev, [stateKey]: false }));
    }
  };

  // Bulk upload handler
  const handleBulkUpload = async (bulkData, targetRevIndex, targetType) => {
    if (!bulkData || bulkData.length === 0) {
      setIsUploading(false);
      alert("No valid rows found in the uploaded file.");
      return;
    }
    setIsUploading(true);
    const revName = `Rev ${targetRevIndex}`;
    const srToValue = {};
    
    // Convert bulkData list to a robust Sr No map
    bulkData.forEach(row => {
        if (typeof row.sr !== 'undefined' && row.sr !== null && row.sr !== '') {
            srToValue[String(Number(row.sr))] = row.value ?? "";
        }
    });

    try {
      const updates = visibleRows.map(row => {
        const srKey = String(Number(row.sr));
        if (srToValue.hasOwnProperty(srKey)) {
          return {
            id: row.id,
            revName,
            value: srToValue[srKey],
            type: targetType,
          };
        }
        return null;
      }).filter(update => update !== null);

      if (updates.length > 0) {
        const updateLocalDrafts = (draftsSetter, existingDrafts) => {
            const next = { ...existingDrafts };
            updates.forEach(u => {
                if (!next[u.id]) next[u.id] = {};
                next[u.id][revName] = u.value;
            });
            draftsSetter(next);
        };
        
        if (targetType === 'response') {
             await onBulkUploadResponse(updates); 
             updateLocalDrafts(setResponseDrafts, responseDrafts);
        } else if (targetType === 'comment') {
            if (typeof onBulkUploadResponse === 'function') {
                 await onBulkUploadResponse(updates);
            }
            updateLocalDrafts(setCommentDrafts, commentDrafts);
        }

        const totalUploadedRows = Object.keys(srToValue).length;
        const updatedGridRows = updates.length;
        setTimeout(() => {
          alert(`Bulk upload completed. Updated ${updatedGridRows} visible rows by matching 'Sr No'. Found ${totalUploadedRows} rows in the file.`);
        }, 100);
      } else {
        alert("No rows in the file matched the visible rows by 'Sr No'.");
      }
    } catch (error) {
      console.error("Bulk upload failed:", error);
      alert("Failed to process bulk upload due to an internal error.");
    } finally {
      setIsUploading(false);
    }
  };

  // Rows data
  const rows = useMemo(() => {
    return clauses
      .filter((c) => c.status !== "Complied")
      .map((c, idx) => ({
        id: c.id ?? idx,
        sr: idx + 1,
        number: c.number || "",
        text: c.text || "",
        status: c.status || "Open",
        original: c,
      }));
  }, [clauses]);

  const visibleRows = useMemo(() => {
    let list = rows;
    if (search && search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((r) => (r.text || "").toLowerCase().includes(s) || (r.number || "").toLowerCase().includes(s));
    }
    if (filter !== "All") {
      if (filter === "Open") list = list.filter((r) => r.status === "Open");
      if (filter === "Closed") list = list.filter((r) => r.status === "Closed");
    }
    return list;
  }, [rows, search, filter]);

  // Visible columns (dynamic)
  const visibleCols = useMemo(() => {
    return columnsMeta.filter((c) => colVisibility[c.id]);
  }, [columnsMeta, colVisibility]);

  // Column management
  const handleShowAll = () => {
    const next = {};
    columnsMeta.forEach((c) => (next[c.id] = true));
    setColVisibility(next);
  };

  const handleDeleteSelectedColumn = () => {
    if (!selectedColumnId) {
      alert("Select a column to delete");
      return;
    }
    if (["sr", "clause", "status"].includes(selectedColumnId)) {
      alert("Core column cannot be deleted");
      return;
    }
    if (!window.confirm("Delete column? This cannot be undone.")) return;
    setColumnsMeta((prev) => prev.filter((c) => c.id !== selectedColumnId));
    setColVisibility((prev) => {
      const n = { ...prev };
      delete n[selectedColumnId];
      return n;
    });
    setSelectedColumnId((prev) => {
      const remaining = columnsMeta.filter((c) => c.id !== prev);
      return remaining.length ? remaining[0].id : null;
    });
  };

  const handleAddColumn = () => {
    const last = columnsMeta[columnsMeta.length - 1];
    const nextRev = last ? (last.type === "response" ? last.revIndex + 1 : last.revIndex) : 0;
    const nextType = last && last.type === "comment" ? "response" : "comment";
    const newCol = nextType === "comment"
      ? { id: `comment-${nextRev}`, type: "comment", revIndex: nextRev, label: `Comment Rev ${nextRev}`, visible: true }
      : { id: `response-${nextRev}`, type: "response", revIndex: nextRev, label: `Customer Response Rev ${nextRev}`, visible: true };
    setColumnsMeta((p) => [...p, newCol]);
    setColVisibility((p) => ({ ...p, [newCol.id]: true }));
    if (nextType === "comment" && typeof onAddRevision === "function") onAddRevision(`Rev ${nextRev}`);
  };

  const handleRevisionChange = (revIndex) => {
    setSelectedRevision(revIndex);
    const newVisibility = { ...colVisibility };
    columnsMeta.forEach((col) => {
      if (col.type === "comment") {
        newVisibility[col.id] = col.revIndex <= revIndex;
      } else if (col.type === "response") {
        newVisibility[col.id] = col.revIndex < revIndex;
      }
    });
    setColVisibility(newVisibility);
  };

  // Export functions
  const exportCSV = () => {
    const header = ["Sr No", "Clause", ...visibleCols.map((c) => c.label), "Status"];
    const rowsCsv = visibleRows.map((r, idx) => {
      const base = [r.sr, `"${(r.text || "").replace(/"/g, '""')}"`];
      visibleCols.forEach((col) => {
        if (col.type === "comment") {
          const txt = getCommentValue(r.original, col.revIndex);
          base.push(`"${String(txt).replace(/"/g, '""')}"`);
        } else {
          const revName = `Rev ${col.revIndex}`;
          const val = (r.original && r.original.customerResponses && r.original.customerResponses[revName]) || "";
          base.push(`"${String(val).replace(/"/g, '""')}"`);
        }
      });
      base.push(r.status || "");
      return base.join(",");
    });
    const csv = [header.join(","), ...rowsCsv].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `${(tender?.clientName || "tender")}_compliance_export.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportDOC = () => {
    const headerLabels = ["Sr No", "Clause", ...visibleCols.map((c) => c.label), "Status"];
    let html = `
        <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
          <meta charset='utf-8'>
          <title>Compliance Review</title>
          <style>
            body { font-family: Arial, sans-serif; }
            table { border-collapse: collapse; width: 100%; }
            th { background-color: #0369a1; color: #fff; padding: 8px; text-align: left; }
            td { padding: 8px; vertical-align: top; border: 1px solid #ccc; text-align: left; }
          </style>
        </head>
        <body>
        <h1>${tender?.clientName || "Tender"} - Compliance Review</h1>
        <table>
        <thead>
            <tr>
                ${headerLabels.map(label => `<th>${label}</th>`).join('')}
            </tr>
        </thead>
        <tbody>
    `;
    visibleRows.forEach((r) => {
      let rowData = [r.sr, r.text || ""];
      visibleCols.forEach((col) => {
        let val;
        if (col.type === "comment") {
          val = getCommentValue(r.original, col.revIndex);
        } else {
          const revName = `Rev ${col.revIndex}`;
          val = (r.original && r.original.customerResponses && r.original.customerResponses[revName]) || "";
        }
        rowData.push(String(val).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>'));
      });
      rowData.push(r.status || "");
      html += `<tr>${rowData.map(data => `<td>${data}</td>`).join('')}</tr>`;
    });
    html += `</tbody></table></body></html>`;
    const filename = `${(tender?.clientName || "tender")}_compliance_export.doc`;
    const blob = new Blob([html], { type: "application/msword;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Reset all row heights
  const resetAllRowHeights = () => {
    setRowHeights({});
    setDefaultRowHeight(180);
  };

  // Calculate total selected
  const selectedCount = Object.values(selectedRows).filter(Boolean).length;

  // Toggle all rows selection
  const toggleSelectAll = (checked) => {
    const newSelected = {};
    if (checked) {
      visibleRows.forEach(row => {
        newSelected[row.id] = true;
      });
    }
    setSelectedRows(newSelected);
  };

  // Get row height
  const getRowHeight = (rowId) => {
    return rowHeights[rowId] || defaultRowHeight;
  };

  return (
    <div className="review-root">
      {/* Controls Bar */}
      <div className="controls">
        <div className="left-controls">
          <select
            className="column-select"
            value={selectedRevision}
            onChange={e => handleRevisionChange(Number(e.target.value))}
            style={{ marginRight: '12px' }}
          >
            {Array.from({ length: maxRevIndex + 1 }, (_, i) => (
              <option key={i} value={i}>Rev {i}</option>
            ))}
          </select>
          <input className="search-input" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
          <div className="quick-filters">
            {["All", "Open", "Closed"].map((f) => (
              <Button key={f} size="small" variant={filter === f ? "contained" : "outlined"} onClick={() => setFilter(f)} sx={{ mr: 1 }}>
                {f}
              </Button>
            ))}
          </div>
          <select className="column-select" value={selectedColumnId || ""} onChange={e => setSelectedColumnId(e.target.value)}>
            {columnsMeta.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
          <Button size="small" onClick={() => setColVisibility((p) => ({ ...p, [selectedColumnId]: !p[selectedColumnId] }))}>
            {colVisibility[selectedColumnId] ? "Hide" : "Show"}
          </Button>
          <Button size="small" onClick={handleShowAll}>Show All</Button>
          <Button size="small" color="error" onClick={handleDeleteSelectedColumn}>Delete Column</Button>
          <Button size="small" onClick={handleAddColumn}>+ Add Column</Button>
        </div>
        <div className="right-controls">
          <div style={{ display: 'flex', alignItems: 'center', marginRight: '16px', gap: '8px' }}>
            <span style={{ fontSize: '12px', color: '#666' }}>Default Height:</span>
            <input
              type="number"
              value={defaultRowHeight}
              onChange={(e) => setDefaultRowHeight(Math.max(80, Math.min(600, Number(e.target.value))))}
              style={{ width: '60px', padding: '4px 6px', borderRadius: '4px', border: '1px solid #e2e8f0' }}
              min={80}
              max={600}
            />
            <span style={{ fontSize: '11px', color: '#94a3b8' }}>px</span>
            <Button size="small" variant="text" onClick={resetAllRowHeights}>
              Reset Heights
            </Button>
          </div>
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportCSV}>
            CSV
          </Button>
          <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={exportDOC} sx={{ ml: 1 }}>
            DOC
          </Button>
        </div>
      </div>

      {/* Info bar */}
      <div className="review-info-bar">
        💡 Drag column borders to resize width. Drag the ↕ handle at the bottom of each row to resize row height.
        {selectedCount > 0 && <span style={{ marginLeft: '16px', color: '#0369a1' }}>{selectedCount} row(s) selected</span>}
      </div>

      {/* Table Container */}
      <div className="review-table-container">
        <table className="review-table-inline" ref={tableRef}>
          <thead>
            <tr>
              <th style={{ width: columnWidths.checkbox, minWidth: 40 }}>
                <input
                  type="checkbox"
                  checked={visibleRows.length > 0 && visibleRows.every(row => selectedRows[row.id])}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                  title="Select all rows"
                />
              </th>
              <th style={{ width: columnWidths.sr, minWidth: 40 }}>
                #
                <span className="col-resize-handle" onMouseDown={(e) => handleColResizeStart(e, 'sr')} />
              </th>
              <th style={{ width: columnWidths.clause, minWidth: 200 }}>
                Clause
                <span className="col-resize-handle" onMouseDown={(e) => handleColResizeStart(e, 'clause')} />
              </th>
              {/* Dynamic Comment/Response Columns */}
              {visibleCols.map((col) => (
                <th key={col.id} style={{ width: columnWidths[col.id] || 280, minWidth: 180 }}>
                  <div className="th-content">
                    <span>{col.label}</span>
                    <UploadButton
                      onUpload={handleBulkUpload}
                      revIndex={col.revIndex}
                      isUploading={isUploading}
                      targetType={col.type}
                    />
                  </div>
                  <span className="col-resize-handle" onMouseDown={(e) => handleColResizeStart(e, col.id)} />
                </th>
              ))}
              <th style={{ width: columnWidths.status, minWidth: 80 }}>
                Status
                <span className="col-resize-handle" onMouseDown={(e) => handleColResizeStart(e, 'status')} />
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 ? (
              <tr>
                <td colSpan={4 + visibleCols.length} style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                  <p>No clauses to review.</p>
                  <p style={{ fontSize: "13px" }}>
                    All clauses have been marked as "Complied" or no clauses match your search.
                  </p>
                </td>
              </tr>
            ) : (
              visibleRows.map((row) => {
                const rowHeight = getRowHeight(row.id);
                return (
                  <tr key={row.id} style={{ height: rowHeight, position: 'relative' }}>
                    <td className="cell-checkbox">
                      <input
                        type="checkbox"
                        checked={!!selectedRows[row.id]}
                        onChange={(e) => setSelectedRows(prev => ({ ...prev, [row.id]: e.target.checked }))}
                      />
                    </td>
                    <td className="cell-sr">{row.sr}</td>
                    <td className="cell-clause" style={{ maxHeight: rowHeight - 20 }}>
                      <div className="clause-cell">
                        <div className="clause-title">
                          {row.number}
                          {clauseSuggestions[row.id] && clauseSuggestions[row.id].length > 0 && (
                            <SuggestionTooltip 
                              suggestions={clauseSuggestions[row.id]}
                              onApply={(value, type) => {
                                if (type === 'comment') {
                                  // Apply the suggested comment to Rev 0 comment draft
                                  const clauseId = row.original.id ?? row.original._id ?? row.original.tempId;
                                  handleCommentChange(clauseId, 0, value);
                                }
                              }}
                            />
                          )}
                        </div>
                        <CollapsibleText text={row.text} lines={4} />
                      </div>
                    </td>
                    {/* Dynamic columns */}
                    {visibleCols.map((col) => {
                      const clause = row.original;
                      const clauseId = clause.id ?? clause._id ?? clause.tempId;
                      const revName = `Rev ${col.revIndex}`;

                      if (col.type === "comment") {
                        if (col.revIndex === 0) {
                          // Rev 0 comment is read-only
                          const txt = getCommentValue(clause, col.revIndex);
                          return (
                            <td key={col.id} className="cell-comment" style={{ maxHeight: rowHeight - 20 }}>
                              <div className="comment-content">
                                {txt || <span style={{ color: '#94a3b8' }}>—</span>}
                              </div>
                            </td>
                          );
                        }
                        const draftVal = (commentDrafts[clauseId] && commentDrafts[clauseId][revName]) ?? getCommentValue(clause, col.revIndex) ?? "";
                        const state = (savingState[clauseId] && savingState[clauseId][`comment-${col.revIndex}`]) || "idle";
                        const aiStateKey = `${clauseId}-comment-${col.revIndex}`;
                        const isAiGenerating = aiGeneratingState[aiStateKey] || false;
                        return (
                          <td key={col.id} className="cell-comment" style={{ maxHeight: rowHeight - 20 }}>
                            <div className="comment-input-wrapper">
                              <TextField
                                multiline
                                minRows={3}
                                value={draftVal}
                                onChange={(e) => handleCommentChange(clauseId, col.revIndex, e.target.value)}
                                onBlur={() => saveComment(clause, col.revIndex)}
                                placeholder="Enter comment..."
                                variant="outlined"
                                size="small"
                                fullWidth
                                disabled={isAiGenerating}
                                sx={{ 
                                  '& .MuiInputBase-root': { fontSize: '13px' },
                                  '& .MuiOutlinedInput-root': { backgroundColor: '#fff' }
                                }}
                              />
                              <Tooltip title="AI Generate: Analyzes clause, previous comments, and your input to create a professional response" arrow placement="top">
                                <IconButton
                                  size="small"
                                  className="ai-generate-btn"
                                  onClick={() => handleAIGenerateRevisionComment(clause, col.revIndex)}
                                  disabled={isAiGenerating}
                                  sx={{
                                    backgroundColor: isAiGenerating ? '#94a3b8' : '#8b5cf6',
                                    color: '#fff',
                                    padding: '4px',
                                    '&:hover': {
                                      backgroundColor: isAiGenerating ? '#94a3b8' : '#7c3aed',
                                    },
                                    '&.Mui-disabled': {
                                      backgroundColor: '#94a3b8',
                                      color: '#fff',
                                    },
                                  }}
                                >
                                  {isAiGenerating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                                </IconButton>
                              </Tooltip>
                            </div>
                            <div className="save-indicator">
                              {state === "saving" && <span className="saving">Saving...</span>}
                              {state === "saved" && <span className="saved">✓ Saved</span>}
                              {state === "error" && <span className="error">Save failed</span>}
                            </div>
                          </td>
                        );
                      } else {
                        // Response column
                        const draftVal = (responseDrafts[clauseId] && responseDrafts[clauseId][revName]) ?? getResponseValue(clause, col.revIndex) ?? "";
                        const state = (responseSavingState[clauseId] && responseSavingState[clauseId][`response-${col.revIndex}`]) || "idle";
                        return (
                          <td key={col.id} className="cell-response" style={{ maxHeight: rowHeight - 20 }}>
                            <TextField
                              multiline
                              minRows={3}
                              value={draftVal}
                              onChange={(e) => handleResponseChange(clauseId, col.revIndex, e.target.value)}
                              onBlur={() => saveResponse(clause, col.revIndex)}
                              placeholder="Enter response..."
                              variant="outlined"
                              size="small"
                              fullWidth
                              sx={{ 
                                '& .MuiInputBase-root': { fontSize: '13px' },
                                '& .MuiOutlinedInput-root': { backgroundColor: '#fff' }
                              }}
                            />
                            <div className="save-indicator">
                              {state === "saving" && <span className="saving">Saving...</span>}
                              {state === "saved" && <span className="saved">✓ Saved</span>}
                              {state === "error" && <span className="error">Save failed</span>}
                            </div>
                          </td>
                        );
                      }
                    })}
                    <td className="cell-status">
                      <Chip
                        label={row.status === "Open" ? "Open" : "Closed"}
                        className={`status-badge ${row.status === "Open" ? "status-open" : "status-closed"}`}
                        size="small"
                        onClick={() => {
                          const newStatus = row.status === "Open" ? "Closed" : "Open";
                          onUpdateStatus(row.id, newStatus);
                        }}
                        sx={{ cursor: "pointer" }}
                      />
                    </td>
                    {/* Row resize handle */}
                    <div 
                      className="row-resize-handle"
                      onMouseDown={(e) => handleRowResizeStart(e, row.id)}
                      title="Drag to resize row height"
                    >
                      <span className="row-resize-icon">↕</span>
                    </div>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
